import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { useStore } from 'src/stores/store';
import Label from 'src/components/Label';
import { IElectricityPayment } from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';
import useThrottle from 'src/hooks/useThrottle';
import { GetMonthName } from 'src/sections/common/GetMonthName';
// ----------------------------------------------------------------------
interface Props {
  year?: number;
  month?: number;
  searchBy?: string;
}
export default observer(function ELectricConsumptionUpdate({ year, month, searchBy }: Props) {
  const { ElectricMeterStore, ElectricityPaymentStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { setOpenCloseConsumptionDialog } = ElectricMeterStore;
  const {
    createElectricityPayment,
    updateElectricityPayment,
    selectedElectricityPaymentDetail,
    loadElectricityPayment,
    setOpenCloseUpdateDialog,
  } = ElectricityPaymentStore;
  const NewRentPaymentSchema = useMemo(
    () =>
      Yup.object().shape({
        // currentElectricMeterDegree: Yup.number()
        //   .typeError(translate('Validation.currentElectricMeterDegree'))
        //   .required(translate('Validation.currentElectricMeterDegree'))
        //   .min(
        //     selectedElectricityPaymentDetail?.lastElectricMeterDegree ?? 0,
        //     translate('Validation.minElectricDegree', {
        //       value: selectedElectricMeter?.lastElectricMeterDegree,
        //     })
        //   ),
      }),
    [selectedElectricityPaymentDetail]
  );

  const defaultValues = useMemo<IElectricityPayment>(
    () => ({
      electricityPaymentId: selectedElectricityPaymentDetail?.electricityPaymentId,
      electricMeterId: selectedElectricityPaymentDetail?.electricMeterId,
      year: Number(year) || undefined,
      month: Number(month) || undefined,
      currentElectricMeterDegree:
        selectedElectricityPaymentDetail?.currentElectricMeterDegree || undefined,
      searchBy: searchBy,
    }),
    [selectedElectricityPaymentDetail, searchBy, year, month]
  );

  const methods = useForm<IElectricityPayment>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    watch,
    setError,
    control,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle((data: IElectricityPayment) => {
    data.electricityPaymentId = selectedElectricityPaymentDetail?.electricityPaymentId;
    data.electricMeterId = selectedElectricityPaymentDetail?.electricmeterId;
    console.log(data);
    updateElectricityPayment(data)
      .then(() => {
        loadElectricityPayment({
          years: Number(year),
          months: Number(month),
          searchBy: searchBy,
        });
        setOpenCloseUpdateDialog();
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error.currentElectricMeterDegree != null) {
          setError('afterSubmit', { ...err, message: json.error.currentElectricMeterDegree });
        } else if (json.message != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  });

  const val = watch();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3} sx={{ p: 3 }}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              {/* {ConsumptionEditMode??(
              <LocalizDatePicker
                name="years"
                label={translate('Report.FromDate')}
                control={control}
                size={'small'}
                views={['year', 'month']}
              />
            )} */}
              <RHFTextField
                name="currentElectricMeterDegree"
                label={translate('ElectricMeter.CurrentDegree')}
                type="number"
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                // startIcon={
                //   !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                // }
                color="warning"
                startIcon={<Iconify icon="eva:edit-fill" />}
              >
                {translate('CRUD.Update')}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseUpdateDialog();
                }}
              >
                {translate('CRUD.Close')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{}}>
            <Label color="success" sx={{ m: 3 }}>
              <Typography variant="body1">
                {GetMonthName(month)}/ {year}
              </Typography>
            </Label>

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>{translate('Customer.Customer')}</TableCell>
                  <TableCell>{selectedElectricityPaymentDetail!.customerName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('Shop.ShopCode')}</TableCell>
                  <TableCell>{selectedElectricityPaymentDetail!.shopCode}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.ElectricMeterSerial')}</TableCell>
                  <TableCell>{selectedElectricityPaymentDetail!.electricMeterSerial}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.LastElectricMeterDegree')}</TableCell>
                  <TableCell>
                    {selectedElectricityPaymentDetail?.previousElectricMeterDegree}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.CurrentDegree')}</TableCell>
                  <TableCell>{val.currentElectricMeterDegree}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.ElectricityPrice')}</TableCell>
                  <TableCell>{selectedElectricityPaymentDetail!.pricePerKilowat}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('ElectricMeter.Usage')}</TableCell>
                  <TableCell>
                    {selectedElectricityPaymentDetail?.previousElectricMeterDegree! <=
                    val.currentElectricMeterDegree!
                      ? Number(val.currentElectricMeterDegree) -
                        Number(selectedElectricityPaymentDetail?.previousElectricMeterDegree)
                      : 0}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>{translate('ElectricMeter.UsagePrice')}</TableCell>
                  <TableCell>
                    {Number(val.currentElectricMeterDegree || 0) >=
                    Number(selectedElectricityPaymentDetail?.previousElectricMeterDegree || 0)
                      ? (Number(val.currentElectricMeterDegree || 0) -
                          Number(
                            selectedElectricityPaymentDetail?.previousElectricMeterDegree || 0
                          )) *
                        Number(selectedElectricityPaymentDetail?.pricePerKilowat || 0)
                      : 0}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
      </Grid>
    </FormProvider>
  );
});
